'use client';

import { Users, ArrowRight } from 'lucide-react';
import { STRIPE_BORDER, STRIPE_SHADOW } from '@/app/(landing)/components/StripeButton';
import Button from '@/components/ui/Button';

// Group-quiz CTA: a compact WHITE card framed by the brand's multicolor
// (cube-hue) border, the same rainbow used on the homepage CTAs. The button
// starts a real group quiz (/group/new), so the copy says exactly that: the
// "group quiz" framing tested clearer and more honest than "compare with
// friends". `className` controls display/order at the call site.
// `detailed` (used on the short-quiz results, which lack the paid-report
// upsell) expands the card with a personalized line + what-you-get bullets so
// it takes more vertical space and the left/right results columns balance.
const DETAILED_POINTS = [
  'Everyone answers the same 12 questions, so the comparison is fair.',
  'You see where you line up, where you split, and who is secretly your opposite.',
  'You get a shared 3D map plus the questions worth arguing about over dinner.',
];

export default function CompareCta({
  onInvite,
  className = '',
  detailed = false,
}: {
  onInvite: () => void;
  className?: string;
  detailed?: boolean;
}) {
  return (
    <div
      className={`rounded-2xl p-[3px] ${className}`}
      style={{ backgroundImage: STRIPE_BORDER, boxShadow: STRIPE_SHADOW }}
    >
      <div className="flex flex-col h-full rounded-[14px] bg-white p-5">
        <div className="flex items-center gap-2 mb-1">
          <Users className="w-5 h-5 text-slate-700" aria-hidden="true" />
          <h3 className="text-lg font-bold text-foreground">Take it as a group quiz</h3>
        </div>
        {detailed ? (
          <>
            <p className="text-sm text-foreground/60 leading-snug mb-3">
              You&apos;ve got your spot. Now see how your friends compare, and where you clash.
            </p>
            <ul className="space-y-2 mb-4">
              {DETAILED_POINTS.map((point) => (
                <li key={point} className="flex items-start gap-2 text-sm text-foreground/70 leading-snug">
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-1.5 shrink-0" aria-hidden="true" />
                  {point}
                </li>
              ))}
            </ul>
          </>
        ) : (
          <p className="text-sm text-foreground/60 leading-snug mb-4">
            Send it to up to 7 friends. Everyone answers the same questions, then you see where you line up, where you split, and what&apos;s worth arguing about.
          </p>
        )}
        <Button
          type="button"
          onClick={onInvite}
          className="mt-auto w-full"
        >
          Start a group quiz <ArrowRight className="w-5 h-5" aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}
